import { useCallback } from "react";
import { applyGlobalMembershipDiscountToBookings } from "./checkoutMemberRateHelpers";
import {
	membershipFlagsFromMemberRateItem,
	nextStateOnDiscountSelect,
	patchBookingItemInCustomerBookings,
} from "./checkoutPricingModifiers";

/**
 * Mutual exclusivity: member rate | member rate (OP) | discount per booking_item_id,
 * plus global "Apply membership discount" override.
 *
 * @param {Object} params
 * @param {Function} params.setMemberRateByBookingItemId
 * @param {Function} params.setDiscountByBookingItemId
 * @param {Function} params.setCustomerBookingItems
 * @param {Function} params.setBookingDetails
 * @param {Function} params.transformCustomerData
 * @param {Object|null} params.bookingDetails
 * @param {boolean} params.publicHoliday
 * @param {boolean} params.isPremiumOutlet
 */
export function useCheckoutPricingModifiers({
	setMemberRateByBookingItemId,
	setDiscountByBookingItemId,
	setCustomerBookingItems,
	customerBookingItems,
	setBookingDetails,
	transformCustomerData,
	bookingDetails,
	publicHoliday,
	isPremiumOutlet = false,
}) {
	const onMemberRateSelect = useCallback(
		(booking, item) => {
			const id = booking?.id;
			if (id == null) return;

			setMemberRateByBookingItemId((prev) => ({ ...prev, [id]: item ?? null }));

			if (item != null) {
				const flags = membershipFlagsFromMemberRateItem(item);
				setDiscountByBookingItemId((prev) => ({ ...prev, [id]: null }));
				setCustomerBookingItems((prev) =>
					patchBookingItemInCustomerBookings(prev, id, {
						...flags,
						isDiscountApplied: false,
						discount_option: null,
						price_adjustment: null,
					})
				);
			} else {
				setCustomerBookingItems((prev) =>
					patchBookingItemInCustomerBookings(prev, id, {
						isMembershipApplied: 0,
						isMemberRateApplied: 0,
					})
				);
			}
		},
		[setMemberRateByBookingItemId, setDiscountByBookingItemId, setCustomerBookingItems]
	);

	const onDiscountSelect = useCallback(
		(booking, item) => {
			const id = booking?.id;
			if (id == null) return;

			const next = nextStateOnDiscountSelect(item);

			setMemberRateByBookingItemId((prev) => ({ ...prev, [id]: next.memberRateOverride }));
			setCustomerBookingItems((prev) =>
				patchBookingItemInCustomerBookings(prev, id, next.bookingPatch)
			);
			if (next.clearBookingMembership) {
				setBookingDetails?.((prev) => (prev ? { ...prev, membership: 0 } : null));
			}
			setDiscountByBookingItemId((prev) => ({ ...prev, [id]: next.discountOverride }));
		},
		[setMemberRateByBookingItemId, setDiscountByBookingItemId, setCustomerBookingItems, setBookingDetails]
	);

	const onGlobalMembershipToggle = useCallback(
		(isChecked) => {
			const isMembershipAppliedValue = isChecked ? 1 : 0;
			const serviceDate = bookingDetails?.service_date;

			setBookingDetails?.((prev) =>
				prev
					? {
							...prev,
							membership: isMembershipAppliedValue,
						}
					: null
			);

			let pack = null;
			if (customerBookingItems && typeof customerBookingItems === "object") {
				const transformed = transformCustomerData(customerBookingItems);
				pack = applyGlobalMembershipDiscountToBookings({
					rawCustomerBookingItems: customerBookingItems,
					transformedForOrder: transformed,
					primaryUserName: bookingDetails?.user?.name,
					memberRateOverrides: {},
					applyMembershipDiscount: isChecked,
					serviceDate,
					publicHoliday,
					isPremiumOutlet,
				});
			}

			if (pack && setCustomerBookingItems) {
				setCustomerBookingItems(pack.nextItems);
			}

			if (isChecked) {
				setDiscountByBookingItemId({});
				if (pack) {
					setMemberRateByBookingItemId(pack.nextMemberOverrides);
				}
			} else if (pack) {
				setMemberRateByBookingItemId(pack.nextMemberOverrides);
			} else {
				setMemberRateByBookingItemId({});
			}
		},
		[
			customerBookingItems,
			bookingDetails?.service_date,
			bookingDetails?.user?.name,
			isPremiumOutlet,
			publicHoliday,
			setBookingDetails,
			setCustomerBookingItems,
			setDiscountByBookingItemId,
			setMemberRateByBookingItemId,
			transformCustomerData,
		]
	);

	return { onMemberRateSelect, onDiscountSelect, onGlobalMembershipToggle };
}
